/**
 * Single source of truth for the HRMS module's navigation.
 *
 * Consumed by:
 *  - `src/components/Navbar/Navbar.tsx` (the left sidebar dropdown) via `flattenHrmsSubTabs()`
 *  - `src/components/Hrms/HrmsPageShell.tsx` (breadcrumb + in-page section sub-nav)
 *
 * Keeping both in one place stops the sidebar and the in-page sub-nav from drifting apart.
 */

import { getUserAccess, hasPermission } from "../../utils/authUtils";

export interface HrmsNavItem {
  label: string;
  path: string;
  /** Hidden from non-admin users (see `getUserAccess().isAdmin`). Applies in addition to `requiredPermission`. */
  adminOnly?: boolean;
  /**
   * When set, visibility requires at least one of these hrmsPermissions keys
   * (see the permission table in experts-db's RBAC design spec). Use this for
   * anything more specific than a flat admin/non-admin split; an `adminOnly`
   * item or section must satisfy both.
   */
  requiredPermission?: string | string[];
}

export interface HrmsSection {
  key: string;
  label: string;
  /** When true the whole section (and its sidebar group) is admin-only, on top of any item `requiredPermission`. */
  adminOnly?: boolean;
  /** Section-level equivalent of `HrmsNavItem.requiredPermission`. */
  requiredPermission?: string | string[];
  items: HrmsNavItem[];
}

/** Shared visibility rule for both the sidebar dropdown and the in-page sub-nav. */
export const isHrmsNavItemVisible = (
  item: HrmsNavItem,
  section?: HrmsSection,
): boolean => {
  const adminOnly = item.adminOnly || section?.adminOnly;
  if (adminOnly && !getUserAccess().isAdmin) return false;
  const requiredPermission = item.requiredPermission ?? section?.requiredPermission;
  if (requiredPermission) {
    const keys = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
    return keys.some((key) => hasPermission(key));
  }
  return true;
};

export const HRMS_SECTIONS: HrmsSection[] = [
  {
    key: "dashboards",
    label: "Dashboards",
    items: [
      { label: "My Dashboard", path: "/hrms/dashboard" },
      {
        label: "Manager Dashboard",
        path: "/hrms/dashboard/manager",
        requiredPermission: "dashboard.manager.view",
      },
      {
        label: "Admin Dashboard",
        path: "/hrms/dashboard/admin",
        requiredPermission: "dashboard.admin.view",
      },
    ],
  },
  {
    key: "me",
    label: "Me",
    items: [
      { label: "My Profile", path: "/hrms/profile" },
      { label: "Monthly Confirmation", path: "/hrms/monthly-confirmation" },
      { label: "Targets", path: "/hrms/targets" },
      { label: "Exit Management", path: "/hrms/exit" },
    ],
  },
  {
    key: "attendance",
    label: "Attendance Management",
    items: [
      { label: "My Attendance", path: "/hrms/attendance" },
      {
        label: "My Team Attendance",
        path: "/hrms/attendance/team",
        requiredPermission: ["attendance.view.team", "attendance.view.all"],
      },
      { label: "Attendance Corrections", path: "/hrms/attendance/corrections" },
    ],
  },
  {
    key: "leave",
    label: "Leave Management",
    items: [
      { label: "My Leaves", path: "/hrms/leaves" },
      { label: "Apply for Leave", path: "/hrms/leaves/apply" },
      {
        label: "Leave Approvals",
        path: "/hrms/leaves/approvals",
        requiredPermission: ["leave.approve.level1", "leave.approve.level2"],
      },
      {
        label: "Team Leave Balance",
        path: "/hrms/leaves/balance",
        requiredPermission: ["attendance.view.team", "attendance.view.all"],
      },
    ],
  },
  {
    key: "reports",
    label: "Reports",
    // Admin and HR Viewer.
    requiredPermission: "reports.view",
    items: [
      { label: "Attendance Reports", path: "/hrms/reports/attendance" },
      { label: "Leave Reports", path: "/hrms/reports/leave" },
    ],
  },
  {
    key: "configuration",
    label: "Configuration",
    items: [
      {
        label: "Manage Employees",
        path: "/hrms/admin/employees",
        requiredPermission: "employees.manage",
      },
      {
        label: "Roles & Permissions",
        path: "/hrms/admin/roles",
        requiredPermission: "roles.manage",
      },
      {
        label: "Holiday Master",
        path: "/hrms/admin/holidays",
        requiredPermission: "config.manage",
      },
      {
        label: "Leave Policy Master",
        path: "/hrms/admin/leave-types",
        requiredPermission: "config.manage",
      },
      {
        label: "Attendance Rule Master",
        path: "/hrms/admin/attendance-rules",
        requiredPermission: "config.manage",
      },
    ],
  },
];

export interface HrmsLocation {
  section: HrmsSection;
  item: HrmsNavItem;
}

/**
 * Resolve a pathname to its HRMS section + item. Matches the longest configured
 * path prefix so nested routes (e.g. `/hrms/leaves/apply`) still resolve, while
 * `/hrms/leaves` keeps matching `My Leaves`.
 */
export const findHrmsLocation = (pathname: string): HrmsLocation | null => {
  let best: HrmsLocation | null = null;
  for (const section of HRMS_SECTIONS) {
    for (const item of section.items) {
      if (pathname === item.path || pathname.startsWith(item.path + "/")) {
        if (!best || item.path.length > best.item.path.length) {
          best = { section, item };
        }
      }
    }
  }
  return best;
};

export const getHrmsSection = (key: string): HrmsSection | undefined =>
  HRMS_SECTIONS.find((s) => s.key === key);

export interface HrmsSubTab {
  label: string;
  path: string;
  group: string;
  adminOnly?: boolean;
  requiredPermission?: string | string[];
}

/** Flat list (with a `group` label per item) for the sidebar dropdown. */
export const flattenHrmsSubTabs = (): HrmsSubTab[] =>
  HRMS_SECTIONS.flatMap((section) =>
    section.items.map((item) => ({
      label: item.label,
      path: item.path,
      group: section.label,
      adminOnly: item.adminOnly || section.adminOnly,
      requiredPermission: item.requiredPermission ?? section.requiredPermission,
    })),
  );
